import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
} from "react";
import { Check, ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import { Button, type ButtonProps } from "../../../components/ui";

/*
 * One layout for every editor menu. Rows share a grid — a 20px icon column,
 * the label, then an optional trailing value, tick or chevron — and the same
 * 10px inset, so icons, labels, fields and tiles line up in every menu. A
 * menu surface sets `data-sheet="true"` (with `group/menu`) for roomier touch
 * sizes. Keyboard focus is drawn in ink so it never reads as a (terracotta)
 * choice.
 */

/** Surface classes for a menu; positioning is left to each menu. */
export const menuSurfaceClass =
  "group/menu rounded-2xl border border-hazumu-border bg-hazumu-white p-1.5 text-hazumu-ink shadow-xl shadow-hazumu-ink/10 scheme-light data-[sheet=true]:rounded-3xl data-[sheet=true]:p-2";

const rowClass =
  "grid w-full grid-cols-[1.25rem_minmax(0,1fr)_auto] items-center gap-x-2.5 rounded-lg px-2.5 text-left text-[13px] leading-5 min-h-9 group-data-[sheet=true]/menu:min-h-11 group-data-[sheet=true]/menu:text-[15px]";

type MenuItemProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  icon?: ReactNode;
  children: ReactNode;
  /** A value, count or chevron on the right. */
  trailing?: ReactNode;
  /** Marks the current choice in a list of options. */
  checked?: boolean;
  tone?: "danger";
};

/** A menu row. The icon column is always kept so labels align. */
export const MenuItem = ({
  icon,
  children,
  trailing,
  checked,
  tone,
  className = "",
  ...props
}: MenuItemProps) => (
  <button
    type="button"
    role={checked === undefined ? "menuitem" : "menuitemradio"}
    aria-checked={checked}
    className={`${rowClass} transition-colors hover:bg-hazumu-canvas focus-visible:bg-hazumu-canvas focus-visible:outline-none disabled:pointer-events-none disabled:opacity-40 [&_svg]:shrink-0 ${tone === "danger" ? "text-hazumu-danger [&>span:first-child]:text-hazumu-danger" : ""} ${className}`}
    {...props}
  >
    <span className="grid place-items-center text-hazumu-muted" aria-hidden="true">
      {icon}
    </span>
    <span className="min-w-0 truncate">{children}</span>
    {(trailing || checked) && (
      <span className="flex items-center gap-1.5 text-xs text-hazumu-muted">
        {trailing}
        {checked && <Check size={16} className="text-hazumu-terracotta" />}
      </span>
    )}
  </button>
);

/** A row that opens a sub-view. */
export const MenuLink = ({
  value,
  ...props
}: Omit<MenuItemProps, "trailing"> & { value?: ReactNode }) => (
  <MenuItem
    {...props}
    trailing={
      <>
        {value}
        <ChevronRight size={16} />
      </>
    }
  />
);

/** A group of rows, with an optional label aligned to the row inset. */
export const MenuSection = ({
  label,
  children,
}: {
  label?: string;
  children: ReactNode;
}) => (
  <div role="group" aria-label={label} className="py-0.5">
    {label && (
      <p className="px-2.5 pt-1.5 pb-1 text-xs text-hazumu-muted">{label}</p>
    )}
    {children}
  </div>
);

export const MenuSeparator = () => (
  <div role="separator" className="mx-2.5 my-1 h-px bg-hazumu-border" />
);

/** A labelled setting on one line: label on the left, control on the right. */
export const MenuField = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="grid min-h-10 grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 px-2.5 group-data-[sheet=true]/menu:min-h-12">
    <span className="text-[13px] text-hazumu-muted group-data-[sheet=true]/menu:text-[15px]">
      {label}
    </span>
    <div className="flex justify-end">{children}</div>
  </div>
);

export const MenuTextInput = (props: InputHTMLAttributes<HTMLInputElement>) => (
  <input
    className="w-full min-w-0 rounded-lg border border-hazumu-border bg-hazumu-white px-2.5 py-1.5 text-[13px] text-hazumu-ink outline-none placeholder:text-hazumu-muted focus:border-hazumu-clay group-data-[sheet=true]/menu:text-base"
    {...props}
  />
);

/** A search box that sits in the row inset. */
export const MenuSearch = (props: InputHTMLAttributes<HTMLInputElement>) => (
  <label className="mx-2.5 my-1.5 flex items-center gap-2 rounded-lg border border-hazumu-border px-2.5 focus-within:border-hazumu-clay">
    <Search size={14} className="shrink-0 text-hazumu-muted" aria-hidden="true" />
    <input
      className="min-w-0 flex-1 bg-transparent py-1.5 text-[13px] text-hazumu-ink outline-none placeholder:text-hazumu-muted group-data-[sheet=true]/menu:text-base"
      {...props}
    />
  </label>
);

/** Two or three mutually exclusive options. */
export function MenuSegmented<T extends string>({
  label,
  options,
  value,
  onChange,
  fullWidth = false,
}: {
  label: string;
  options: ReadonlyArray<readonly [T, ReactNode]>;
  value: T;
  onChange: (value: T) => void;
  /** Stretch across the row, splitting it evenly between the options. */
  fullWidth?: boolean;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={`flex rounded-lg bg-hazumu-canvas p-0.5 ${fullWidth ? "w-full [&>button]:flex-1 [&>button]:justify-center" : ""}`}
    >
      {options.map(([option, text]) => (
        <button
          key={option}
          type="button"
          role="radio"
          aria-checked={value === option}
          onClick={() => onChange(option)}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs whitespace-nowrap text-hazumu-muted transition-colors hover:text-hazumu-ink focus-visible:outline-2 focus-visible:outline-hazumu-ink aria-checked:bg-hazumu-white aria-checked:text-hazumu-ink aria-checked:shadow-sm group-data-[sheet=true]/menu:px-3 group-data-[sheet=true]/menu:py-1.5 group-data-[sheet=true]/menu:text-[13px]"
        >
          {text}
        </button>
      ))}
    </div>
  );
}

/**
 * Colour choices as round swatches, six to a row; each option's className
 * paints it. Extra attributes land on the group (a site theme, say, so
 * theme-relative swatches show that theme's colours).
 */
export function MenuSwatches<T extends string>({
  label,
  options,
  value,
  onChange,
  ...attributes
}: {
  label: string;
  /** `tone` lands as data-tone, for swatches drawn by attribute. */
  options: ReadonlyArray<{ id: T; label: string; className: string; mark?: ReactNode; tone?: string }>;
  value: string;
  onChange: (value: T) => void;
} & Omit<HTMLAttributes<HTMLDivElement>, "onChange">) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="grid grid-cols-6 gap-1.5 px-2.5 pt-0.5 pb-1.5"
      {...attributes}
    >
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          role="radio"
          aria-label={option.label}
          title={option.label}
          aria-checked={value === option.id}
          data-tone={option.tone}
          onClick={() => onChange(option.id)}
          className={`grid aspect-square place-items-center rounded-full border border-hazumu-border text-xs text-hazumu-muted ring-offset-2 aria-checked:ring-2 aria-checked:ring-hazumu-terracotta focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hazumu-ink ${option.className}`}
        >
          {option.mark}
        </button>
      ))}
    </div>
  );
}

/**
 * Style choices as tiles that sketch their own arrangement; the sketches are
 * drawn from data-layout in DocumentEditor.css.
 */
export function MenuTiles<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Record<T, string>;
  value: string;
  onChange: (value: T) => void;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="grid grid-cols-3 gap-1.5 px-2.5 pt-0.5 pb-1.5">
      {(Object.keys(options) as T[]).map((option) => (
        <button
          key={option}
          type="button"
          role="radio"
          data-layout={option}
          aria-checked={value === option}
          onClick={() => onChange(option)}
          className="flex flex-col items-stretch gap-1.5 rounded-lg border border-hazumu-border px-2 pt-2 pb-1.5 text-center text-xs text-hazumu-muted transition-colors hover:border-hazumu-clay focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-hazumu-ink aria-checked:border-hazumu-terracotta aria-checked:text-hazumu-ink aria-checked:ring-1 aria-checked:ring-hazumu-terracotta"
        >
          <span className="block-menu-layout-preview" aria-hidden="true">
            <i />
            <i />
            <i />
            <i />
          </span>
          {options[option]}
        </button>
      ))}
    </div>
  );
}

/**
 * Heads a sub-view or panel. Its back (or close) button sits in the icon
 * column so the title lines up with the labels below.
 */
export const MenuHeader = ({
  title,
  onBack,
  onClose,
}: {
  title: string;
  onBack?: () => void;
  onClose?: () => void;
}) => (
  <div className="mb-1 grid min-h-9 grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-x-2.5 border-b border-hazumu-border px-2.5 pb-1.5 group-data-[sheet=true]/menu:min-h-11">
    <button
      type="button"
      aria-label={onClose ? "Close" : "Back"}
      onClick={onClose ?? onBack}
      className="-m-1.5 grid size-8 place-items-center rounded-lg text-hazumu-muted hover:bg-hazumu-canvas hover:text-hazumu-ink focus-visible:outline-2 focus-visible:outline-hazumu-ink"
    >
      {onClose ? <X size={16} /> : <ChevronLeft size={16} />}
    </button>
    <strong className="truncate text-[13px] font-semibold group-data-[sheet=true]/menu:text-[15px]">
      {title}
    </strong>
  </div>
);

/** A menu's form or footer button. */
export const MenuButton = ({
  variant = "ghost",
  ...props
}: Omit<ButtonProps, "size" | "variant"> & { variant?: "primary" | "ghost" }) => (
  <Button size="sm" variant={variant} {...props} />
);

/** A menu panel pinned above the bottom of the screen, for short tasks. */
export const floatingMenuClass = `${menuSurfaceClass} fixed bottom-5 left-1/2 z-70 max-h-[65dvh] w-[min(22.5rem,calc(100%-1.5rem))] -translate-x-1/2 overflow-auto overscroll-contain`;

/** Explanatory text, aligned with labels. */
export const MenuNote = ({ children }: { children: ReactNode }) => (
  <p className="px-2.5 py-1.5 text-xs leading-5 text-hazumu-muted">{children}</p>
);
